import { useAppService, useSelectedOrgId } from "@fero/hooks";
import { fromUseQueryResult } from "@fero/utils/loading-values";
import type { AppService } from "adl-gen/app-service";
import {
  makeStockDataReq,
  type StockDataCounts,
  type StockListing,
} from "adl-gen/ferovinum/app/api";
import type { TopLevelUnitType } from "adl-gen/ferovinum/app/db";
import { Loader } from "components/widgets/loader/loader";
import { groupBy } from "lodash";
import { useCallback, useMemo } from "react";
import { useLoadingDataState } from "utils/hooks/use-loading-data";
import { assertNotUndefined } from "utils/hx/util/types";
import { isLoaded } from "utils/utility-types";
import { useGetStockDataCountsQuery } from "../../../../hooks/react-query/queries/use-get-stock-data-counts.query";
import { OrganisationDashboardPageView } from "./organisation-dashboard-page-view";
import type { StockDataDisplayInfo } from "./stock-listing-table";

export const makeStockDataMapDisplayInfo = (
  stockDataCounts: StockDataCounts,
): StockDataDisplayInfo => {
  const stockDataMap: Map<string, StockListing[]> = new Map();
  return {
    ...stockDataCounts,
    stockDataMap,
  };
};

const STOCK_DATA_QUERY_LIMIT = 500;

export const OrganisationDashboardPage = () => {
  const service: AppService = useAppService();
  const selectedOrgId = assertNotUndefined(useSelectedOrgId());

  const loadMoreStockDataMap = useCallback(
    async (topLevelUnitType: TopLevelUnitType, offset: number) => {
      const stockDataResp = await service.getStockData(
        makeStockDataReq({
          organisationId: selectedOrgId,
          topLevelUnitTypeOption: {
            kind: "byUnitType",
            value: topLevelUnitType,
          },
          offset,
          count: STOCK_DATA_QUERY_LIMIT,
        }),
      );
      return new Map(
        Object.entries(groupBy(stockDataResp, (x) => x.product.id)),
      );
    },
    [service, selectedOrgId],
  );

  const loadingStockData = fromUseQueryResult(useGetStockDataCountsQuery());

  const stockCountsByType = useMemo<
    Record<TopLevelUnitType, StockDataDisplayInfo>
  >(
    () =>
      (isLoaded(loadingStockData)
        ? Object.fromEntries(
            loadingStockData.value
              .filter((entry) => entry.value.totalProductCount > 0)
              .map(({ key, value }) => [
                key,
                makeStockDataMapDisplayInfo(value),
              ]),
          )
        : {}) as Record<TopLevelUnitType, StockDataDisplayInfo>,
    [loadingStockData],
  );

  const loadImportantReminders = useCallback(
    async () =>
      await service.getOrganisationImportantReminders({
        organisationId: selectedOrgId,
      }),
    [selectedOrgId, service],
  );
  const [loadingImportantReminders] = useLoadingDataState(
    loadImportantReminders,
  );

  return (
    <Loader loadingStates={[loadingStockData]} fullScreen>
      {isLoaded(loadingStockData) && (
        <OrganisationDashboardPageView
          stockDataLookup={stockCountsByType}
          loadingImportantReminders={loadingImportantReminders}
          loadMoreStockDataMap={loadMoreStockDataMap}
        />
      )}
    </Loader>
  );
};
